import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/home";

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const count = heroSlides.length;

  const go = (next: number) => setCurrent((next + count) % count);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % count);
    }, 6000);
    return () => clearInterval(timer);
  }, [count]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? current + 1 : current - 1);
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[480px] md:h-[620px] overflow-hidden bg-primary-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === current ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.image}
            alt={`${slide.title} - 邻居的妻子影视大全焦点推荐`}
            title={`${slide.title} - 剧情介绍`}
            className="w-full h-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex items-center">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-2xl">
            {heroSlides.map((slide, i) => (
              <div key={slide.id} className={i === current ? "block" : "hidden"}>
                <div className="animate-fade-up">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-bold mb-4">
                    <i className="ri-fire-line"></i>
                    {slide.tag}
                  </span>
                  <h2 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">
                    {slide.title}
                  </h2>
                  <p className="mt-3 text-base md:text-lg text-accent-200 font-medium">{slide.slogan}</p>
                  <p className="mt-3 text-sm md:text-base text-background-200/90 leading-relaxed max-w-xl">
                    {slide.description}
                  </p>
                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/detail/${slide.id}`}
                      className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer"
                    >
                      <i className="ri-play-circle-line text-lg"></i>
                      查看剧情详情
                    </Link>
                    <Link
                      to="/category"
                      className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300/50 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer"
                    >
                      浏览全部影视
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(current - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-primary-950/40 border border-background-200/30 text-background-50 hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(current + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-primary-950/40 border border-background-200/30 text-background-50 hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => setCurrent(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === current ? "w-8 bg-accent-500" : "w-3 bg-background-200/50 hover:bg-background-200/80"
            }`}
            aria-label={`切换到第${i + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}